/**
 * Discovery's three glyphs: an office block for the company lists, a beacon
 * for the signals and a set of sliders for the scoring profiles.
 *
 * Drawn the way `components/icons.tsx` draws: a 24x24 stroke path on the same
 * grid, 1.75 stroke, round caps and joins, decorative by default and sized by
 * the same class, so one sits level with its neighbours in the sidebar rows,
 * the rail and the page titles.
 *
 * Kept here rather than added to `components/icons.tsx`, which is shared and
 * edited from several directions, because their only callers are the Discovery
 * pages, the sidebar's Discovery rows and the Discovery settings entry, the
 * way the Prospecting glyphs live beside the prospects pages.
 */
import type { ReactNode, SVGProps } from 'react'
import { cn } from '../../lib/cn'
import type { IconProps } from '../../components/icons'

function Icon({ className, children, ...props }: SVGProps<SVGSVGElement> & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn('h-[1.15em] w-[1.15em] shrink-0', className)}
      {...props}
    >
      {children}
    </svg>
  )
}

/** Company lists: an office block with a lower wing beside it. */
export const IconCompanyList = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.5 20.5h17" />
    <path d="M5.5 20.5V5.5A1.5 1.5 0 0 1 7 4h6.5A1.5 1.5 0 0 1 15 5.5v15" />
    <path d="M15 9.5h2.5A1.5 1.5 0 0 1 19 11v9.5" />
    <path d="M8.5 8h3M8.5 11.5h3M8.5 15h3" />
  </Icon>
)

/** Signals: a point with two rings of waves going out from it. */
export const IconSignals = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="1.5" />
    <path d="M8.5 15.5a5 5 0 0 1 0-7M15.5 8.5a5 5 0 0 1 0 7" />
    <path d="M5.6 18.4a9 9 0 0 1 0-12.8M18.4 5.6a9 9 0 0 1 0 12.8" />
  </Icon>
)

/** Scoring: three sliders set at different points. */
export const IconScoring = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 6.5h9M17.5 6.5h2M4.5 12h2M10.5 12h9M4.5 17.5h7M15.5 17.5h4" />
    <circle cx="15.5" cy="6.5" r="2" />
    <circle cx="8.5" cy="12" r="2" />
    <circle cx="13.5" cy="17.5" r="2" />
  </Icon>
)
